import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addOwner,
  listAdmins,
  listOwners,
  removeOwner,
  type OwnerAdminRecord,
  type Admin,
} from "../../../helpers/api/crm";

interface Props {
  customerId: string;
}

/**
 * Compact multi-owner picker for the 360 "Admin actions" row. Chips with
 * remove affordance; a dropdown lets the admin add another userType='A'.
 * Mutations invalidate the owners list + the dashboard list so the table
 * reflects the change immediately.
 */
export function OwnersSection({ customerId }: Props) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState("");

  const { data: ownersData, isLoading: loadingOwners } = useQuery<{
    owners: OwnerAdminRecord[];
  }>({
    queryKey: ["crm", "customers", customerId, "owners"],
    queryFn: () => listOwners(customerId),
  });

  const { data: adminsData } = useQuery<{ admins: Admin[] }>({
    queryKey: ["crm", "admins"],
    queryFn: listAdmins,
    staleTime: 5 * 60 * 1000,
    enabled: adding,
  });

  const add = useMutation({
    mutationFn: (adminId: string) => addOwner(customerId, adminId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["crm", "customers", customerId, "owners"],
      });
      qc.invalidateQueries({ queryKey: ["crm", "customers"] });
      setAdding(false);
      setSelected("");
    },
  });

  const remove = useMutation({
    mutationFn: (ownershipId: string) => removeOwner(customerId, ownershipId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["crm", "customers", customerId, "owners"],
      });
      qc.invalidateQueries({ queryKey: ["crm", "customers"] });
    },
  });

  const owners = ownersData?.owners ?? [];
  const ownerAdminIds = new Set(owners.map((o) => o.adminId));
  const pickable =
    adminsData?.admins.filter((a) => !ownerAdminIds.has(a.id)) ?? [];

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
        <h2 className="text-sm font-semibold text-content-primary">Owners</h2>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-xs font-medium text-primary hover:text-primary/80"
          >
            + Add
          </button>
        )}
      </div>

      <div className="flex-1 p-4">
        {loadingOwners ? (
          <div className="h-6 w-28 bg-gray-100 rounded animate-pulse" />
        ) : owners.length === 0 && !adding ? (
          <p className="text-xs text-content-tertiary italic">
            No owners assigned.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {owners.map((o) => (
              <span
                key={o.ownershipId}
                className="inline-flex items-center gap-1.5 pl-1.5 pr-1 py-0.5 rounded-full bg-gray-100 text-xs text-content-primary"
              >
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold">
                  {(o.adminFirstName?.[0] ?? o.adminEmail[0]).toUpperCase()}
                </span>
                <span className="truncate max-w-[100px]">
                  {o.adminFirstName ?? o.adminEmail}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${o.adminEmail}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(o.ownershipId)}
                  className="w-4 h-4 rounded-full hover:bg-gray-200 inline-flex items-center justify-center text-content-tertiary hover:text-content-primary disabled:opacity-50 leading-none"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {adding && (
          <div className="flex flex-col gap-2 mt-3">
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full text-xs px-2 py-1.5 border border-border-secondary rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            >
              <option value="">Select admin…</option>
              {pickable.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.firstName ? `${a.firstName} — ${a.email}` : a.email}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={!selected || add.isPending}
                onClick={() => selected && add.mutate(selected)}
                className="text-xs px-3 py-1 bg-primary text-white rounded-md disabled:opacity-50 font-medium"
              >
                {add.isPending ? "Adding…" : "Add"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAdding(false);
                  setSelected("");
                }}
                className="text-xs px-2 py-1 text-content-tertiary hover:text-content-primary"
              >
                Cancel
              </button>
            </div>
            {add.isError && (
              <span className="text-[11px] text-rose-600">
                Failed — {(add.error as Error).message}
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
